import React, { useEffect, useRef, useState } from 'react';
import { Camera, Edit3, MapPin, Briefcase, GraduationCap, Shield, Plus, X, Trash2, LogOut } from 'lucide-react';
import { interestsList } from '../data/mockData';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import ProfilePhoto from './ProfilePhoto';
import { apiService, formatApiError } from '../services/api';

const MAX_PHOTOS = 6;

const Profile = () => {
  const { logout } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showInterestSelector, setShowInterestSelector] = useState(false);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    try {
      const res = await apiService.users.getMe();
      setUser(res.data.data);
    } catch (e) {
      toast({ title: 'Could not load profile', description: formatApiError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setEditForm({ name: user.name || '', bio: user.bio || '', profession: user.profession || '', education: user.education || '', interests: user.interests || [] });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiService.users.updateMe({
        name: editForm.name.trim(), bio: editForm.bio, profession: editForm.profession || null,
        education: editForm.education || null, interests: editForm.interests,
      });
      setUser(res.data.data);
      setIsEditing(false);
      toast({ title: 'Profile Updated', description: 'Your changes have been saved.' });
    } catch (e) {
      toast({ title: 'Save failed', description: formatApiError(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if ((user.images?.length || 0) + files.length > MAX_PHOTOS) {
      toast({ title: 'Too many photos', description: `You can have up to ${MAX_PHOTOS} photos.`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      await apiService.users.uploadPhotos(files);
      await load();
      toast({ title: 'Photos uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: formatApiError(e), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (url) => {
    const photoId = url.startsWith('/api/photos/') ? url.split('/').pop() : encodeURIComponent(url);
    try {
      await apiService.users.deletePhoto(photoId);
      setUser((u) => ({ ...u, images: u.images.filter((i) => i !== url) }));
    } catch (e) {
      toast({ title: 'Could not remove photo', description: formatApiError(e), variant: 'destructive' });
    }
  };

  const toggleInterest = (interest) => {
    setEditForm((f) => ({ ...f, interests: f.interests.includes(interest) ? f.interests.filter((i) => i !== interest) : f.interests.length < 20 ? [...f.interests, interest] : f.interests }));
  };

  if (loading) return <div className="text-center py-12 text-gray-500" data-testid="profile-loading">Loading profile…</div>;
  if (!user) return <div className="text-center py-12 text-red-500" data-testid="profile-error">Profile unavailable.</div>;

  return (
    <div className="space-y-6" data-testid="profile-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <div className="flex space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)} disabled={saving} data-testid="profile-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !editForm.name?.trim()} className="bg-gradient-to-r from-pink-500 to-red-500 text-white" data-testid="profile-save-btn">{saving ? 'Saving…' : 'Save'}</Button>
            </>
          ) : (
            <Button variant="outline" onClick={startEdit} data-testid="profile-edit-btn"><Edit3 className="w-4 h-4 mr-2" />Edit</Button>
          )}
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Photos <span className="text-sm text-gray-500">({user.images?.length || 0}/{MAX_PHOTOS})</span></h2>
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading || (user.images?.length || 0) >= MAX_PHOTOS} data-testid="profile-add-photo-btn">
            <Camera className="w-4 h-4 mr-2" />{uploading ? 'Uploading…' : 'Add photo'}
          </Button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleImageUpload} data-testid="profile-photo-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {(user.images || []).map((url) => (
            <div key={url} className="relative aspect-[3/4] rounded-lg overflow-hidden bg-gray-100">
              <ProfilePhoto src={url} alt="" compact />
              <button onClick={() => removeImage(url)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1" data-testid="profile-remove-photo-btn"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {(user.images || []).length === 0 && <p className="col-span-3 text-sm text-gray-500 text-center py-6">No photos yet. Add at least one so people can see you.</p>}
        </div>
        <p className="text-xs text-gray-500 mt-3">JPEG, PNG or WebP · up to 5MB · at least 200×200px</p>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
        {isEditing ? (
          <div className="space-y-4">
            <Input value={editForm.name} maxLength={50} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" data-testid="profile-name-input" />
            <Textarea value={editForm.bio} maxLength={500} rows={4} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} placeholder="Tell people about yourself" data-testid="profile-bio-input" />
            <p className="text-xs text-gray-500 text-right">{editForm.bio?.length || 0}/500</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-lg font-semibold" data-testid="profile-name">{user.name}, {user.age}</p>
            <p className="flex items-center text-gray-600"><MapPin className="w-4 h-4 mr-2" />{user.location}</p>
            <p className="text-gray-700 whitespace-pre-wrap" data-testid="profile-bio">{user.bio || 'No bio yet.'}</p>
            {user.is_verified && <Badge className="bg-blue-100 text-blue-700"><Shield className="w-3 h-3 mr-1" />Verified</Badge>}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Professional & Education</h2>
        {isEditing ? (
          <div className="space-y-3">
            <Input value={editForm.profession} maxLength={100} onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })} placeholder="Profession" data-testid="profile-profession-input" />
            <Input value={editForm.education} maxLength={100} onChange={(e) => setEditForm({ ...editForm, education: e.target.value })} placeholder="Education" data-testid="profile-education-input" />
          </div>
        ) : (
          <div className="space-y-2 text-gray-700">
            <p className="flex items-center"><Briefcase className="w-4 h-4 mr-2 text-gray-500" />{user.profession || 'Not specified'}</p>
            <p className="flex items-center"><GraduationCap className="w-4 h-4 mr-2 text-gray-500" />{user.education || 'Not specified'}</p>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Interests</h2>
          {isEditing && <Button variant="outline" size="sm" onClick={() => setShowInterestSelector(true)} data-testid="profile-add-interest-btn"><Plus className="w-4 h-4 mr-1" />Edit</Button>}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="profile-interests">
          {((isEditing ? editForm.interests : user.interests) || []).map((i) => (
            <Badge key={i} variant="secondary" className="px-3 py-1">{i}{isEditing && <button onClick={() => toggleInterest(i)} className="ml-1"><X className="w-3 h-3" /></button>}</Badge>
          ))}
          {((isEditing ? editForm.interests : user.interests) || []).length === 0 && <p className="text-sm text-gray-500">No interests added yet.</p>}
        </div>
      </Card>

      {showInterestSelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold">Choose interests</h3><button onClick={() => setShowInterestSelector(false)}><X className="w-5 h-5" /></button></div>
            <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto">
              {interestsList.map((i) => (
                <button key={i} onClick={() => toggleInterest(i)} className={`px-3 py-1 rounded-full text-sm border ${editForm.interests.includes(i) ? 'bg-pink-500 text-white border-pink-500' : 'border-gray-300'}`} data-testid={`interest-option-${i}`}>{i}</button>
              ))}
            </div>
            <Button className="w-full mt-4" onClick={() => setShowInterestSelector(false)}>Done</Button>
          </div>
        </div>
      )}

      <Button variant="outline" className="w-full text-red-600 border-red-200" onClick={logout} data-testid="profile-logout-btn"><LogOut className="w-4 h-4 mr-2" />Log out</Button>
    </div>
  );
};

export default Profile;
